import { useState, useEffect } from 'react'
import { useAdminAuth } from '../hooks/useAdminAuth'
import { useQuestionnaire } from '../hooks/useQuestionnaire'
import QuestionnaireVersionHistory from './QuestionnaireVersionHistory'
import QuestionnaireEditorFlow from './QuestionnaireEditor/QuestionnaireEditorFlow'
import { FileText, History, Edit, AlertCircle, CheckCircle } from 'lucide-react'

function QuestionnaireManager() {
  const { isEditor, isAdmin } = useAdminAuth()
  const { questionnaire, loading, error, refetch } = useQuestionnaire()
  const [activeTab, setActiveTab] = useState('current') // 'current', 'history', 'editor'

  // Refetch active questionnaire when switching to 'current' tab
  useEffect(() => {
    if (activeTab === 'current') {
      refetch()
    }
  }, [activeTab])

  if (!isEditor()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
            Accès refusé
          </h2>
          <p className="text-gray-600 text-center">
            Vous devez avoir le rôle d'éditeur ou d'administrateur pour accéder à cette page.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Gestion du Questionnaire
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Gérez les versions et le contenu du questionnaire d'évaluation
              </p>
            </div>
            {questionnaire && (
              <div className="bg-blue-50 px-4 py-2 rounded-lg">
                <p className="text-sm text-gray-600">Version active</p>
                <p className="text-lg font-semibold text-blue-600">
                  v{questionnaire.version || '1.0.0'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('current')}
              className={`${
                activeTab === 'current'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <FileText className="w-5 h-5 mr-2" />
              Version Actuelle
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <History className="w-5 h-5 mr-2" />
              Historique des Versions
            </button>
            {isEditor() && (
              <button
                onClick={() => setActiveTab('editor')}
                className={`${
                  activeTab === 'editor'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                <Edit className="w-5 h-5 mr-2" />
                Éditeur
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'current' && (
          <CurrentVersionView questionnaire={questionnaire} loading={loading} error={error} />
        )}
        
        {activeTab === 'history' && (
          <QuestionnaireVersionHistory onVersionActivated={refetch} />
        )}
        
        {activeTab === 'editor' && (
          <QuestionnaireEditorFlow />
        )}
      </div>
    </div>
  )
}

// Current Version View Component
function CurrentVersionView({ questionnaire, loading, error }) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-600">Chargement...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <p className="text-red-700">Erreur: {error}</p>
        </div>
      </div>
    )
  }

  if (!questionnaire) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" />
          <p className="text-yellow-700">Aucun questionnaire actif trouvé</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Overview Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{questionnaire.title}</h2>
            <p className="mt-1 text-gray-600">{questionnaire.description}</p>
          </div>
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
        
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Thèmes</p>
            <p className="text-3xl font-bold text-blue-600">{questionnaire.themes?.length || 0}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Questions</p>
            <p className="text-3xl font-bold text-purple-600">
              {questionnaire.themes?.reduce((sum, theme) => sum + (theme.questions?.length || 0), 0) || 0}
            </p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Score Maximum</p>
            <p className="text-3xl font-bold text-green-600">{questionnaire.totalMaxScore || 0}</p>
          </div>
        </div>
      </div>

      {/* Themes List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Thèmes du Questionnaire</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {questionnaire.themes?.map((theme, index) => (
            <div key={theme.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{theme.icon}</span>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">{theme.title}</h4>
                    <p className="text-sm text-gray-500">
                      {theme.questions?.length || 0} questions • Score max: {theme.maxScore}
                    </p>
                  </div>
                </div>
                <span className="text-sm text-gray-400">#{index + 1}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}


export default QuestionnaireManager

